import type { TFunction } from "i18next";
import { ReactNode } from "react";

import { getCourseInfoTranslationKey } from "@ryde/controls";
import {
  ICoursesCalendarSlotDto,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
} from "@ryde/shared";

import { getCoursesCalendarCourseRouteSummaryLabel } from "./courses-calendar-cell.utils";

export interface IBuildCoursesCalendarRideRouteTooltip {
  t: TFunction;
  slot: ICoursesCalendarSlotDto;
  onRideIdClick: (id: number) => void;
}

export const buildCoursesCalendarRideRouteTooltip = ({
  t,
  slot,
  onRideIdClick,
}: IBuildCoursesCalendarRideRouteTooltip): ReactNode => {
  const rideId = slot.rideId;
  if (!slot.hasRide || typeof rideId !== "number") {
    return null;
  }
  const from = slot.originStationName ?? t("course.calendar.noFrom");
  const to = slot.destinationStationName ?? t("course.calendar.noTo");
  return (
    <span>
      {t("booking.CALENDAR_ROUTED_STATUS_TOOLTIP_FROM", { fromStation: from })}
      <br />
      {t("booking.CALENDAR_ROUTED_STATUS_TOOLTIP_TO", { toStation: to })}
      <br />
      <span>
        {t("booking.CALENDAR_ROUTED_STATUS_TOOLTIP_RIDE")}{" "}
        <span
          className="cc-ride-id-link"
          role="button"
          tabIndex={0}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onRideIdClick(rideId);
          }}
          onKeyDown={onActivateKeyDown(e => {
            e.preventDefault();
            e.stopPropagation();
            onRideIdClick(rideId);
          })}
        >
          {rideId}
        </span>
      </span>
    </span>
  );
};

export interface IBuildCoursesCalendarCourseRouteTypeTooltip {
  t: TFunction;
  slot: ICoursesCalendarSlotDto;
}

export const buildCoursesCalendarCourseRouteTypeTooltip = ({
  t,
  slot,
}: IBuildCoursesCalendarCourseRouteTypeTooltip): ReactNode => {
  const routeType = slot.courseRouteType;
  const courseLine = `${String(slot.courseId)}: ${slot.courseName}`;
  const routeSummary = getCoursesCalendarCourseRouteSummaryLabel(t, slot);
  const routeSummaryLine = routeSummary ? (
    <p className="cc-tooltip-course-meta">{routeSummary}</p>
  ) : null;

  if (!routeType) {
    return (
      <>
        <span className="cc-tooltip-course-line">
          {NO_VALUE_DISPLAY} | {courseLine}
        </span>
        {routeSummaryLine}
      </>
    );
  }
  const shortLabel = t(`selectListLabel.${routeType}`);
  const detailKey = getCourseInfoTranslationKey(routeType);
  return (
    <>
      <p className="cc-tooltip-course-title">{`${shortLabel} | ${courseLine}`}</p>
      <p className="cc-tooltip-course-detail">{t(detailKey)}</p>
      {routeSummaryLine}
    </>
  );
};
